import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { firstValueFrom } from 'rxjs';

export interface Fotografia {
  id: string;
  titulo: string;
  descricao: string;
  imagemUrl?: string;
}

export interface FotografiaAdmin extends Fotografia {
  ativa: boolean;
  criada_em: string;
  votos: number;
}

export interface MeuVoto {
  votou: boolean;
  fotografia_id: string | null;
  votacao_aberta: boolean;
}

export interface FotografiaPodio extends Fotografia { votos: number; }
export interface Podio { liberado: boolean; fotos: FotografiaPodio[]; }

@Injectable({ providedIn: 'root' })
export class ConcursoFotografiasService {
  private readonly http = inject(HttpClient);
  private readonly api = `${environment.apiUrl}/api/concurso-fotografias`;

  async listar(): Promise<Fotografia[]> {
    return this.comImagens(await firstValueFrom(this.http.get<Fotografia[]>(`${this.api}/fotos`)));
  }

  async meuVoto(): Promise<MeuVoto> {
    return firstValueFrom(this.http.get<MeuVoto>(`${this.api}/meu-voto`));
  }

  async votar(fotografiaId: string): Promise<void> {
    await firstValueFrom(this.http.post<void>(`${this.api}/votos`, { fotografia_id: fotografiaId }));
  }

  async listarAdministracao(ordem: 'ALFABETICA' | 'VOTOS' = 'ALFABETICA'): Promise<FotografiaAdmin[]> {
    const fotos = await firstValueFrom(this.http.get<FotografiaAdmin[]>(`${this.api}/admin/fotos`, { params: { ordem } }));
    return this.comImagens(fotos);
  }

  async podio(): Promise<Podio> {
    const podio = await firstValueFrom(this.http.get<Podio>(`${this.api}/podio`));
    return { ...podio, fotos: await this.comImagens(podio.fotos) };
  }

  async podioAdministracao(): Promise<boolean> {
    const status = await firstValueFrom(this.http.get<{ liberado: boolean }>(`${this.api}/admin/podio`));
    return status.liberado;
  }

  async alterarPodio(liberado: boolean): Promise<boolean> {
    const status = await firstValueFrom(this.http.put<{ liberado: boolean }>(`${this.api}/admin/podio`, { liberado }));
    return status.liberado;
  }

  async votacaoAdministracao(): Promise<boolean> {
    const status = await firstValueFrom(this.http.get<{ aberta: boolean }>(`${this.api}/admin/votacao`));
    return status.aberta;
  }

  async alterarVotacao(aberta: boolean): Promise<boolean> {
    const status = await firstValueFrom(this.http.put<{ aberta: boolean }>(`${this.api}/admin/votacao`, { aberta }));
    return status.aberta;
  }

  async criar(titulo: string, descricao: string, foto: File): Promise<void> {
    const form = new FormData();
    form.append('titulo', titulo);
    form.append('descricao', descricao);
    form.append('foto', foto);
    await firstValueFrom(this.http.post<void>(`${this.api}/admin/fotos`, form));
  }

  async atualizar(foto: FotografiaAdmin): Promise<void> {
    await firstValueFrom(this.http.put<void>(`${this.api}/admin/fotos/${foto.id}`, {
      titulo: foto.titulo,
      descricao: foto.descricao,
      ativa: foto.ativa,
    }));
  }

  async alterarStatus(id: string, ativa: boolean): Promise<void> {
    await firstValueFrom(this.http.patch<void>(`${this.api}/admin/fotos/${id}/ativa`, { ativa }));
  }

  async excluir(id: string): Promise<void> {
    await firstValueFrom(this.http.delete<void>(`${this.api}/admin/fotos/${id}`));
  }

  mensagemErro(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.message || error.message || 'Não foi possível concluir a operação.';
    }
    return 'Não foi possível concluir a operação.';
  }

  private async comImagens<T extends Fotografia>(fotos: T[]): Promise<T[]> {
    return Promise.all(fotos.map(async foto => {
      const blob = await firstValueFrom(this.http.get(`${this.api}/fotos/${foto.id}/imagem`, { responseType: 'blob' }));
      return { ...foto, imagemUrl: URL.createObjectURL(blob) };
    }));
  }
}
